/**
 * The channel sidebar's drag and drop, with pointer events (mouse and pen;
 * a touch screen gets no drag: Server settings › Channels has a Reorder
 * mode instead). A press becomes a drag after `DRAG_THRESHOLD` px, so a
 * click still opens the channel; Escape cancels a drag, and the click a
 * drag ends with never reaches the row.
 *
 * - A person under a voice channel, dropped on another voice channel: moved
 *   there (`moveMemberFromUi`). The channels they may be moved to light up
 *   and the rest dim; a drop anywhere else does nothing.
 * - A channel or a category header (Manage Channels): sorted, with a drop
 *   line where it lands (`sidebarDnd.ts`), committed at once
 *   (`commitChannelDrop`) and put back if the server refuses.
 *
 * Near the list's top or bottom edge the list scrolls by itself.
 */
import { ChannelType, type Snowflake } from '@neosocial/protocol';
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useData } from '../store/data';
import { canMoveMemberTo } from '../store/moveMember';
import { channelDropBody, type ChannelDrop } from '../store/reorder';
import { channelIcon, channelTree, userName } from '../store/selectors';
import { Avatar, Icon, type IconName } from '../ui/kit';
import {
  autoScrollStep,
  canDragMember,
  canSortChannels,
  categoryDropFor,
  channelDropFor,
  commitChannelDrop,
  dropPlace,
  memberDropTargets,
  pastThreshold,
  useSidebarDrag,
  type CategoryHit,
  type ChannelHit,
  type SidebarDrag,
} from './sidebarDnd';
import { moveMemberFromUi } from './voiceUi';

type Source = { kind: 'member'; userId: Snowflake } | { kind: 'channel'; id: Snowflake };

interface Gesture {
  pointerId: number;
  source: Source;
  x0: number;
  y0: number;
  x: number;
  y: number;
  /** pending: not a drag yet; dragging; cancelled (Escape): waits for the release. */
  phase: 'pending' | 'dragging' | 'cancelled';
}

interface Ghost {
  label: string;
  icon?: IconName;
  userId?: Snowflake;
}

/** How far (px) the ghost sits from the pointer, so the row under it stays visible. */
const GHOST_OFFSET = 12;

const setDrag = (drag: SidebarDrag | null) => useSidebarDrag.setState({ drag });

/** The ghost follows the pointer (a style write, not a render, on every move). */
function placeGhost(el: HTMLElement, p: { x: number; y: number }) {
  el.style.transform = `translate(${p.x + GHOST_OFFSET}px, ${p.y + GHOST_OFFSET}px)`;
}

/** The rows a channel drag is placed among, in screen order. */
function sortRows(box: HTMLElement): HTMLElement[] {
  return [
    ...box.querySelectorAll<HTMLElement>(
      '.ns-cat-row > .cat-h[data-cat], .cat-list > li > .ch[data-ch]',
    ),
  ];
}

/** What is under `y` for a dragged channel (`ChannelHit`). */
function channelHit(box: HTMLElement, y: number): ChannelHit {
  for (const row of sortRows(box)) {
    if (row.matches('.cat-h')) {
      const r = (row.parentElement ?? row).getBoundingClientRect();
      if (y < r.bottom)
        return { kind: 'head', catId: row.dataset.cat!, after: y >= r.top + r.height / 2 };
      continue;
    }
    const li = (row.parentElement ?? row).getBoundingClientRect();
    if (y < li.bottom) {
      const r = row.getBoundingClientRect();
      return { kind: 'channel', id: row.dataset.ch!, after: y >= r.top + r.height / 2 };
    }
  }
  return { kind: 'end' };
}

/** What is under `y` for a dragged category (`CategoryHit`). */
function categoryHit(box: HTMLElement, y: number): CategoryHit {
  for (const sec of box.querySelectorAll<HTMLElement>('section[data-cat-sec]')) {
    const r = sec.getBoundingClientRect();
    if (y < r.bottom) {
      const id = sec.dataset.catSec!;
      return {
        kind: 'section',
        catId: id === 'none' ? null : id,
        after: y >= r.top + r.height / 2,
      };
    }
  }
  return { kind: 'end' };
}

/** The voice channel whose row (with the people under it) is under the pointer. */
function voiceChannelAt(box: HTMLElement, y: number): Snowflake | null {
  for (const ch of box.querySelectorAll<HTMLElement>('.cat-list > li > .ch[data-type="voice"]')) {
    const r = (ch.parentElement ?? ch).getBoundingClientRect();
    if (y >= r.top && y < r.bottom) return ch.dataset.ch ?? null;
  }
  return null;
}

/** The drop line's top in the list's content for a drop place, or null. */
function lineTop(
  box: HTMLElement,
  place: { parentId: Snowflake | null; beforeId: Snowflake | null; afterId: Snowflake | null },
  category: boolean,
): number {
  const top = box.getBoundingClientRect().top - box.scrollTop;
  const sel = (id: string) =>
    category
      ? box.querySelector<HTMLElement>(`section[data-cat-sec="${id}"]`)
      : (box.querySelector<HTMLElement>(`.ch[data-ch="${id}"]`)?.parentElement ?? null);
  // half the gap between the rows: 2px between channels, 16px between categories
  const half = category ? 8 : 1;
  const before = place.beforeId ? sel(place.beforeId) : null;
  if (before) return before.getBoundingClientRect().top - half - top;
  const after = place.afterId ? sel(place.afterId) : null;
  if (after) return after.getBoundingClientRect().bottom + half - top;
  if (place.parentId) {
    const head = box.querySelector<HTMLElement>(`.cat-h[data-cat="${place.parentId}"]`);
    if (head) return (head.parentElement ?? head).getBoundingClientRect().bottom + 2 - top;
  }
  return 4;
}

export function useSidebarDnd(
  ref: RefObject<HTMLDivElement | null>,
  guildId: Snowflake | null,
): { line: ReactNode; ghost: ReactNode } {
  const [ghost, setGhost] = useState<Ghost | null>(null);
  const ghostEl = useRef<HTMLDivElement | null>(null);
  const pos = useRef({ x: 0, y: 0 });
  const gidRef = useRef(guildId);
  useEffect(() => {
    gidRef.current = guildId;
  });
  useEffect(() => {
    const box = ref.current;
    if (!box) return;
    let g: Gesture | null = null;
    let raf = 0;

    /** Where the drag is now: the target under the pointer, the line. */
    const update = () => {
      const drag = useSidebarDrag.getState().drag;
      if (!g || g.phase !== 'dragging' || !drag) return;
      pos.current = { x: g.x, y: g.y };
      if (ghostEl.current) placeGhost(ghostEl.current, pos.current);
      const r = box.getBoundingClientRect();
      const inside = g.x >= r.left && g.x <= r.right;
      const y = Math.max(r.top, Math.min(r.bottom - 1, g.y));
      if (drag.kind === 'member') {
        const at = inside ? voiceChannelAt(box, y) : null;
        const over = at && drag.targets.includes(at) ? at : null;
        if (over !== drag.over) setDrag({ ...drag, over });
        return;
      }
      const d = useData.getState();
      const dragged = d.channels[drag.id];
      const tree = channelTree(d, drag.guildId);
      let drop: ChannelDrop | null = null;
      if (inside && dragged)
        drop = drag.category
          ? categoryDropFor(tree, drag.id, categoryHit(box, y))
          : channelDropFor(tree, drag.id, channelHit(box, y));
      // a drop that changes nothing (or one the server would refuse) is no drop: no line
      if (drop && !channelDropBody(d.channels, drag.guildId, drop).length) drop = null;
      const line =
        drop && dragged
          ? Math.round(lineTop(box, dropPlace(tree, dragged, drop), drag.category))
          : null;
      if (line !== drag.line || JSON.stringify(drop) !== JSON.stringify(drag.drop))
        setDrag({ ...drag, drop, line });
    };

    const tick = () => {
      raf = 0;
      if (!g || g.phase !== 'dragging') return;
      const r = box.getBoundingClientRect();
      const inside = g.x >= r.left - 24 && g.x <= r.right + 24;
      const dy = inside ? autoScrollStep(g.y, r.top, r.bottom) : 0;
      if (dy) {
        const was = box.scrollTop;
        box.scrollTop = was + dy;
        if (box.scrollTop !== was) update();
      }
      raf = requestAnimationFrame(tick);
    };

    const start = (): boolean => {
      if (!g) return false;
      const d = useData.getState();
      const gid = gidRef.current;
      if (!gid) return false;
      if (g.source.kind === 'member') {
        const { userId } = g.source;
        if (!canDragMember(d, gid, userId)) return false;
        setDrag({
          kind: 'member',
          guildId: gid,
          userId,
          from: d.voiceStates[gid]?.[userId]?.channel_id ?? '',
          targets: memberDropTargets(d, gid, userId),
          over: null,
        });
        setGhost({ label: userName(d, userId, gid), userId });
      } else {
        const c = d.channels[g.source.id];
        if (!c || c.guild_id !== gid || !canSortChannels(d, gid)) return false;
        const category = c.type === ChannelType.GUILD_CATEGORY;
        setDrag({ kind: 'channel', guildId: gid, id: c.id, category, drop: null, line: null });
        setGhost({ label: c.name ?? '', icon: channelIcon(c.type) });
      }
      g.phase = 'dragging';
      document.documentElement.classList.add('ns-dragging');
      window.getSelection?.()?.removeAllRanges();
      window.addEventListener('keydown', onKey, true);
      if (typeof requestAnimationFrame === 'function') raf = requestAnimationFrame(tick);
      return true;
    };

    /** Ends the drag's look (the store, the ghost, the page's class). */
    const clear = () => {
      if (raf && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(raf);
      raf = 0;
      window.removeEventListener('keydown', onKey, true);
      document.documentElement.classList.remove('ns-dragging');
      setDrag(null);
      setGhost(null);
    };

    /** Ends the gesture: no more listening to the pointer. */
    const finish = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('blur', onCancel);
      if (g && g.phase !== 'pending') clear();
      g = null;
    };

    const onMove = (e: PointerEvent) => {
      if (!g || e.pointerId !== g.pointerId) return;
      g.x = e.clientX;
      g.y = e.clientY;
      if (g.phase === 'pending') {
        if (!pastThreshold(g.x - g.x0, g.y - g.y0)) return;
        // not yours to drag: the press stays an ordinary one
        if (!start()) return finish();
      }
      if (g.phase === 'dragging') {
        e.preventDefault();
        update();
      }
    };

    /** The click a drag ends with goes nowhere (the row under it would open). */
    const eatClick = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const swallowNextClick = () => {
      window.addEventListener('click', eatClick, true);
      setTimeout(() => window.removeEventListener('click', eatClick, true), 0);
    };

    const onUp = (e: PointerEvent) => {
      if (!g || e.pointerId !== g.pointerId) return;
      const was = g;
      const drag = useSidebarDrag.getState().drag;
      if (was.phase !== 'pending') swallowNextClick();
      finish();
      if (was.phase !== 'dragging' || !drag) return;
      if (drag.kind === 'member') {
        // checked again now: they may have left, or been moved, meanwhile
        if (drag.over && canMoveMemberTo(useData.getState(), drag.guildId, drag.userId, drag.over))
          void moveMemberFromUi(drag.guildId, drag.userId, drag.over);
      } else if (drag.drop) void commitChannelDrop(drag.guildId, drag.drop);
    };

    const onCancel = () => finish();

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !g || g.phase !== 'dragging') return;
      e.preventDefault();
      e.stopPropagation();
      g.phase = 'cancelled';
      clear();
    };

    const onDown = (e: PointerEvent) => {
      // the main button of a mouse or a pen; never touch (it scrolls the list)
      if (e.button !== 0 || e.pointerType === 'touch') return;
      // a press whose release never came (it ended outside the window) ends here
      if (g) finish();
      const t = e.target as Element | null;
      const member = t?.closest<HTMLElement>('.vc-u[data-user]');
      const ch = member ? null : t?.closest<HTMLElement>('.ch[data-ch], .cat-h[data-cat]');
      let source: Source | null = null;
      if (member && box.contains(member)) source = { kind: 'member', userId: member.dataset.user! };
      else if (ch && box.contains(ch))
        source = { kind: 'channel', id: ch.dataset.ch ?? ch.dataset.cat! };
      if (!source) return;
      g = {
        pointerId: e.pointerId,
        source,
        x0: e.clientX,
        y0: e.clientY,
        x: e.clientX,
        y: e.clientY,
        phase: 'pending',
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onCancel);
      window.addEventListener('blur', onCancel);
    };

    // an avatar image would start the browser's own drag (and cancel ours)
    const onNativeDrag = (e: DragEvent) => e.preventDefault();

    box.addEventListener('pointerdown', onDown);
    box.addEventListener('dragstart', onNativeDrag);
    return () => {
      box.removeEventListener('pointerdown', onDown);
      box.removeEventListener('dragstart', onNativeDrag);
      finish();
    };
  }, [ref]);

  const line = useSidebarDrag((s) => (s.drag?.kind === 'channel' ? s.drag.line : null));
  const d = useData.getState();
  return {
    line:
      line !== null ? (
        <div className="ns-dnd-line" style={{ top: line }} aria-hidden="true" />
      ) : null,
    ghost:
      ghost && typeof document !== 'undefined'
        ? createPortal(
            <div
              className="nx-raised ns-drag-ghost"
              aria-hidden="true"
              ref={(el) => {
                ghostEl.current = el;
                if (el) placeGhost(el, pos.current);
              }}
            >
              {ghost.userId ? (
                <Avatar user={d.users[ghost.userId]} name={ghost.label} size={20} />
              ) : ghost.icon ? (
                <Icon name={ghost.icon} />
              ) : null}
              <span dir="auto">{ghost.label}</span>
            </div>,
            document.body,
          )
        : null,
  };
}
